<script setup>
import { ref } from 'vue';
import { useField } from 'vee-validate';
import api from '@/services/axios';
import { useToast } from 'primevue/usetoast';
import { useFormValidation } from '@/composables/useFormValidation';
import { forgotPasswordSchema } from '@/formValidationschemas';
import InputError from '@/components/general/InputError.vue';
import InputLabel from '@/components/general/InputLabel.vue';
import PrimaryButton from '@/components/general/PrimaryButton.vue';

const toast = useToast();
const processing = ref(false);
const emailSent = ref(false);
const serverErrors = ref({});

// Initialize form with vee-validate
const initialValues = {
    email: '',
};

const { handleSubmit, errors, resetForm } = useFormValidation(
    forgotPasswordSchema,
    initialValues
);

// Setup form fields with useField
const { value: email } = useField('email');

const submit = handleSubmit(async (values) => {
    processing.value = true;
    serverErrors.value = {};
    emailSent.value = false;

    try {
        await api.post('/forgot-password', {
            email: values.email
        });

        // Reset form after successful submission
        resetForm();
        emailSent.value = true;

        toast.add({
            severity: 'success',
            summary: 'Email Sent',
            detail: 'Password reset link has been sent to your email',
            life: 5000
        });
    } catch (error) {
        if (error.response && error.response.data && error.response.data.errors) {
            // Store backend validation errors
            serverErrors.value = error.response.data.errors;

            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to send password reset link',
                life: 3000
            });
        } else {
            // Generic error handling
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'An unexpected error occurred',
                life: 3000
            });
        }
    } finally {
        processing.value = false;
    }
});
</script>

<template>
    <div class="flex flex-col justify-center items-center px-4">
        <!-- Card Container -->
        <div class="p-8 w-full max-w-md bg-white rounded-2xl shadow-lg">
            <h2 class="mb-6 text-2xl font-bold text-gray-900">Forgot Password</h2>

            <div class="mb-6 text-sm text-gray-600">
                Forgot your password? No problem. Just let us know your email address and we will email you a password reset link that will allow you to choose a new one.
            </div>

            <!-- Success Message -->
            <div v-if="emailSent" class="p-4 mb-6 text-sm text-green-700 bg-green-100 rounded-lg">
                A password reset link has been sent to your email address.
            </div>

            <form @submit.prevent="submit" v-if="!emailSent">
                <div class="mb-6">
                    <InputLabel for="email" value="Email" />

                    <input
                        id="email"
                        v-model="email"
                        type="email"
                        class="block px-4 py-3 mt-1 w-full text-gray-900 rounded-xl border-0 ring-1 ring-inset ring-gray-300 shadow-sm placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        required
                        autofocus
                        autocomplete="username"
                    />

                    <InputError :message="errors.email || (serverErrors.email ? serverErrors.email[0] : '')" class="mt-2" />
                </div>

                <div class="flex justify-end items-center mt-6">
                    <PrimaryButton type="submit" :disabled="processing">
                        Email Password Reset Link
                    </PrimaryButton>
                </div>
            </form>

            <!-- Back to Login Link -->
            <div class="mt-6 text-center">
                <RouterLink
                    :to="{ name: 'login' }"
                    class="text-sm font-medium text-green-600 hover:text-green-500"
                >
                    Back to Login
                </RouterLink>
            </div>
        </div>
    </div>
</template>
